import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fly/core';
import { toastService } from '@fly/shared';
import { closeDialog, useAppDispatch } from '@fly/store';

import { Environment, useDeleteEnvironmentMutation } from '../../store';

export function EnvironmentDeleteConfirmation({
  environment,
}: {
  environment: Environment;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [deleteEnvironment, { isLoading: isDeletingEnvironment }] =
    useDeleteEnvironmentMutation();

  const handleSubmit = useCallback(() => {
    deleteEnvironment(environment.name).then((response) => {
      if (response.error) {
        toastService.error(
          t(
            'app.dashboard.runtime_environments.environment_form.errors.failed_to_delete',
            { defaultValue: 'Failed to delete environment' }
          )
        );
        return;
      }

      dispatch(closeDialog());
      toastService.success(
        t(
          'app.dashboard.runtime_environments.environments.delete_confirmation.deleted_successfully',
          {
            defaultValue: 'Environment deleted successfully',
          }
        )
      );
    });
  }, [deleteEnvironment, environment.name, dispatch, t]);

  return (
    <div>
      <Text size='2' color='neutral'>
        {t(
          'app.dashboard.runtime_environments.delete_confirmation.description',
          {
            defaultValue:
              'This action is irreversible and permanently remove it and all related data.',
          }
        )}
      </Text>
      <div className='mt-fly-4 flex flex-row justify-end gap-fly-3'>
        <Button
          size='2'
          variant='soft'
          color='neutral'
          onClick={() => dispatch(closeDialog())}
        >
          {t(
            'app.dashboard.runtime_environments.environment_delete_confirmation.buttons.cancel',
            { defaultValue: 'Cancel' }
          )}
        </Button>
        <Button
          size='2'
          variant='soft'
          color='error'
          data-testid='runtime-environments-delete-env-btn'
          loading={isDeletingEnvironment}
          onClick={handleSubmit}
        >
          {t(
            'app.dashboard.runtime_environments.environment_delete_confirmation.buttons.delete',
            { defaultValue: 'Delete' }
          )}
        </Button>
      </div>
    </div>
  );
}
